// Admins management — list, add-by-phone, remove-with-confirm.
// Purpose: every admin is visible + auditable inside the app, not just in Mongo.
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2 } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

export default function AdminsTab({ currentUserId }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/admins");
      setAdmins(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load admins");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { toast.error("Enter a mobile number"); return; }
    setBusy(true);
    try {
      await api.post("/admin/admins", { mobile: phone.trim(), name: name.trim() || undefined });
      toast.success("Admin added");
      setPhone(""); setName("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add admin"); }
    finally { setBusy(false); }
  };

  const removeAdmin = async (row) => {
    const ok = window.confirm(`Remove admin access for ${row.name} (${row.phone})?`);
    if (!ok) return;
    try {
      await api.delete(`/admin/admins/${row.id}`);
      toast.success("Admin removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not remove admin"); }
  };

  return (
    <div className="space-y-6" data-testid="admins-tab">
      <div className="card-default">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-primary"/>
          <h3 className="heading text-xl">Add admin</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          The new admin will be able to sign in from <code>/admin</code> using OTP on this number. No SMS is sent at creation.
        </p>
        <form onSubmit={addAdmin} className="grid sm:grid-cols-[2fr,2fr,auto] gap-3 items-end" data-testid="add-admin-form">
          <div>
            <Label htmlFor="new-admin-phone">Mobile number *</Label>
            <Input
              id="new-admin-phone"
              type="tel" inputMode="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+91 98181 51515"
              data-testid="new-admin-phone"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="new-admin-name">Name (optional)</Label>
            <Input
              id="new-admin-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Full name"
              data-testid="new-admin-name"
              className="mt-1"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="rounded-full bg-primary hover:bg-primary/90 h-10 px-5"
            data-testid="add-admin-submit"
          >
            <Plus size={14} className="mr-1"/> {busy ? "Adding…" : "Add admin"}
          </Button>
        </form>
      </div>

      <div className="card-default">
        <div className="flex items-center justify-between mb-4">
          <h3 className="heading text-xl">Admins <Badge variant="outline" className="rounded-full ml-2">{admins.length}</Badge></h3>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
        ) : admins.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No admins yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map(a => (
                  <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="tabular-nums">{a.phone}</TableCell>
                    <TableCell>{fmtDate(a.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm" variant="ghost"
                        className="text-rose-600 hover:bg-rose-50 rounded-full"
                        disabled={a.id === currentUserId}
                        title={a.id === currentUserId ? "You can't remove your own admin access" : "Remove admin access"}
                        onClick={() => removeAdmin(a)}
                        data-testid={`remove-admin-${a.id}`}
                      >
                        <Trash2 size={14} className="mr-1"/> Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
